import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Center,
  ChakraProvider,
  Container,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Switch,
  IconButton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import AdminHeader from '../components/AdminHeader';
import { deleteBlog, getBlogCategories, getBlogs, setBlogReadVisibility, setBlogStatus } from '../blog/blogApi';
import { isReadCountVisible } from '../blog/blogForm';

function ClippedText({ value, weight = '400' }) {
  const text = String(value || '').trim();
  const label = (
    <Text
      display="block"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      fontWeight={weight}
      color={weight === '600' ? 'gray.800' : 'gray.700'}
      cursor={text ? 'help' : 'default'}
    >
      {text || '—'}
    </Text>
  );
  if (!text) return label;
  return (
    <Tooltip label={text} hasArrow placement="top" openDelay={200}>
      <Box overflow="hidden" maxW="100%">
        {label}
      </Box>
    </Tooltip>
  );
}

function UpdatedAt({ value }) {
  if (!value) return <Text color="gray.500">—</Text>;
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return <Text color="gray.600">{value}</Text>;
  const day = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return (
    <VStack align="flex-start" spacing={0} lineHeight="1.3">
      <Text fontSize="sm" color="gray.700" whiteSpace="nowrap">{day}</Text>
      <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{time}</Text>
    </VStack>
  );
}

function ActionIcon({ children }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

function BlogManagement() {
  const navigate = useNavigate();
  const toast = useToast();
  const cancelRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [visibilityId, setVisibilityId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    total_records: 0,
    total_pages: 1,
    current_page: 1,
    has_next_page: false,
    has_prev_page: false,
  });

  useEffect(() => {
    document.title = 'Blog Management';
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await getBlogCategories();
      setCategories(Array.isArray(data.categories) ? data.categories : []);
    } catch (error) {
      setCategories([]);
    }
  }, []);

  const loadBlogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getBlogs({
        page,
        limit: 10,
        search: debouncedSearch,
        category,
        status,
      });
      setBlogs(Array.isArray(data.blogs) ? data.blogs : []);
      setPagination({
        total_records: data.pagination?.total_records || 0,
        total_pages: data.pagination?.total_pages || 1,
        current_page: data.pagination?.current_page || page,
        has_next_page: data.pagination?.has_next_page || false,
        has_prev_page: data.pagination?.has_prev_page || false,
      });
    } catch (error) {
      setBlogs([]);
      toast({
        description: error.message || 'Failed to load blog posts.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      if (/authentication/i.test(error.message || '')) {
        navigate('/Admin');
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, category, status, navigate, toast]);

  useEffect(() => {
    if (!localStorage.getItem('adminToken')) {
      navigate('/Admin');
      return;
    }
    loadBlogs();
  }, [loadBlogs, navigate]);

  useEffect(() => {
    if (localStorage.getItem('adminToken')) {
      loadCategories();
    }
  }, [loadCategories]);

  const changePage = (nextPage) => {
    if (nextPage < 1 || nextPage > (pagination.total_pages || 1)) return;
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmDelete = (blog) => {
    setPendingDelete(blog);
    onOpen();
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await deleteBlog(pendingDelete.id);
      toast({
        description: 'Blog post deleted.',
        status: 'success',
        duration: 2500,
        isClosable: true,
        position: 'bottom',
      });
      onClose();
      setPendingDelete(null);
      if (blogs.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        loadBlogs();
      }
      loadCategories();
    } catch (error) {
      toast({
        description: error.message || 'Unable to delete this blog post.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleReadVisibility = async (blog, nextVisible) => {
    const previous = isReadCountVisible(blog.show_read_count);
    setVisibilityId(blog.id);
    setBlogs((rows) => rows.map((row) => (
      row.id === blog.id ? { ...row, show_read_count: nextVisible } : row
    )));
    try {
      const result = await setBlogReadVisibility(blog.id, nextVisible);
      setBlogs((rows) => rows.map((row) => (
        row.id === blog.id
          ? {
            ...row,
            show_read_count: typeof result?.show_read_count === 'boolean' ? result.show_read_count : nextVisible,
            read_count: Number.isFinite(Number(result?.read_count)) ? Number(result.read_count) : row.read_count,
          }
          : row
      )));
      toast({
        description: result?.message || (nextVisible ? 'Reader count is now visible on this blog.' : 'Reader count is hidden on this blog.'),
        status: 'success',
        duration: 2500,
        isClosable: true,
        position: 'bottom',
      });
    } catch (error) {
      setBlogs((rows) => rows.map((row) => (
        row.id === blog.id ? { ...row, show_read_count: previous } : row
      )));
      toast({
        description: error.message || 'Unable to save reader count visibility.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom',
      });
      if (/authentication/i.test(error.message || '')) {
        navigate('/Admin');
      }
    } finally {
      setVisibilityId(null);
    }
  };

  const toggleStatus = async (blog) => {
    const nextStatus = blog.status === 'published' ? 'draft' : 'published';
    setBusyId(blog.id);
    try {
      const result = await setBlogStatus(blog.id, nextStatus);
      toast({
        description: result.message || (nextStatus === 'published' ? 'Blog post published.' : 'Blog post unpublished.'),
        status: 'success',
        duration: 2500,
        isClosable: true,
        position: 'bottom',
      });
      loadBlogs();
    } catch (error) {
      toast({
        description: error.message || 'Unable to update publication status.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = pagination.total_pages || 1;

  return (
    <ChakraProvider>
      <Box minH="100vh" bg="gray.50">
        <AdminHeader
          fullWidth
          activeTab="blog"
          title="Blog Management"
          badge={
            <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
              {pagination.total_records} Total
            </Badge>
          }
        />
        <Container maxW="full" px={{ base: 4, md: 6 }} pb={8}>
          <VStack spacing={6} align="stretch">
            <Card borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200">
              <CardBody p={6}>
                <Flex justify="space-between" align="center" gap={4} flexWrap="wrap" mb={4}>
                  <Text color="gray.600">
                    Create and manage legal case studies. Drafts stay off the public website.
                  </Text>
                  <Button
                    colorScheme="blue"
                    borderRadius="md"
                    fontWeight="600"
                    onClick={() => navigate('/Admin/Blog/new')}
                  >
                    Add New Blog
                  </Button>
                </Flex>
                <Flex gap={4} flexWrap="wrap">
                  <InputGroup flex="1" minW="260px">
                    <InputLeftElement pointerEvents="none" color="gray.400">
                      Search
                    </InputLeftElement>
                    <Input
                      pl="4.5rem"
                      placeholder="Search by title or case name"
                      value={searchTerm}
                      onChange={(event) => {
                        setSearchTerm(event.target.value);
                        setPage(1);
                      }}
                      borderRadius="md"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                    />
                  </InputGroup>
                  <Select
                    maxW="240px"
                    value={category}
                    onChange={(event) => {
                      setCategory(event.target.value);
                      setPage(1);
                    }}
                    borderRadius="md"
                    border="2px solid"
                    borderColor="gray.200"
                    aria-label="Filter by category"
                  >
                    <option value="">All categories</option>
                    {categories.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Select>
                  <Select
                    maxW="200px"
                    value={status}
                    onChange={(event) => {
                      setStatus(event.target.value);
                      setPage(1);
                    }}
                    borderRadius="md"
                    border="2px solid"
                    borderColor="gray.200"
                    aria-label="Filter by publication status"
                  >
                    <option value="all">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </Select>
                </Flex>
              </CardBody>
            </Card>

            <Card borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
              <Box overflowX="auto">
                <Table variant="simple" size="md" w="100%" sx={{ tableLayout: 'fixed' }}>
                  <Thead bg="gray.50">
                    <Tr>
                      {[
                        ['Title', '22%'],
                        ['Case Name', '18%'],
                        ['Court', '16%'],
                        ['Year', '72px'],
                        ['Category', '12%'],
                        ['Status', '108px'],
                        ['Reads', '72px'],
                        ['Show count', '120px'],
                        ['Last Updated', '128px'],
                        ['Actions', '168px'],
                      ].map(([heading, width]) => (
                        <Th key={heading} w={width} color="gray.600" fontWeight="700" fontSize="xs" letterSpacing="0.04em" textTransform="uppercase" py={4} whiteSpace="nowrap" overflow="hidden">
                          {heading}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {isLoading ? (
                      <Tr>
                        <Td colSpan={10}>
                          <Center py={10}>
                            <VStack>
                              <Spinner color="blue.500" />
                              <Text color="gray.500">Loading blog posts...</Text>
                            </VStack>
                          </Center>
                        </Td>
                      </Tr>
                    ) : blogs.length === 0 ? (
                      <Tr>
                        <Td colSpan={10} textAlign="center" py={12}>
                          <Text color="gray.500" fontWeight="600">No blog posts found</Text>
                          <Text color="gray.400" fontSize="sm" mt={1}>
                            {searchTerm || category || status !== 'all'
                              ? 'Try a different search or filter.'
                              : 'Add a case study to get started.'}
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      blogs.map((blog) => (
                        <Tr key={blog.id} _hover={{ bg: 'gray.50' }}>
                          <Td py={4} overflow="hidden" maxW="0">
                            <ClippedText value={blog.title} weight="600" />
                          </Td>
                          <Td overflow="hidden" maxW="0">
                            <ClippedText value={blog.case_name} />
                          </Td>
                          <Td overflow="hidden" maxW="0">
                            <ClippedText value={blog.court} />
                          </Td>
                          <Td color="gray.700">{blog.year_of_decision || '—'}</Td>
                          <Td overflow="hidden" maxW="0">
                            <ClippedText value={blog.category} />
                          </Td>
                          <Td>
                            <Badge colorScheme={blog.status === 'published' ? 'green' : 'orange'} borderRadius="full" px={2}>
                              {blog.status === 'published' ? 'Published' : 'Draft'}
                            </Badge>
                          </Td>
                          <Td fontWeight="700" color="gray.800" whiteSpace="nowrap">
                            {Number(blog.read_count) || 0}
                          </Td>
                          <Td whiteSpace="nowrap">
                            <HStack spacing={2}>
                              <Switch
                                size="sm"
                                colorScheme="green"
                                isChecked={isReadCountVisible(blog.show_read_count)}
                                isDisabled={visibilityId === blog.id}
                                aria-label={isReadCountVisible(blog.show_read_count) ? 'Hide reader count on this blog' : 'Show reader count on this blog'}
                                onChange={(event) => toggleReadVisibility(blog, event.target.checked)}
                              />
                              <Text fontSize="xs" fontWeight="700" color={isReadCountVisible(blog.show_read_count) ? 'green.600' : 'gray.500'} w="28px">
                                {isReadCountVisible(blog.show_read_count) ? 'ON' : 'OFF'}
                              </Text>
                            </HStack>
                          </Td>
                          <Td>
                            <UpdatedAt value={blog.updated_at} />
                          </Td>
                          <Td>
                            <HStack spacing={1} flexWrap="nowrap" whiteSpace="nowrap">
                              <Tooltip label="Edit" hasArrow>
                                <IconButton
                                  aria-label="Edit"
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="blue"
                                  icon={<ActionIcon><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5z" /></ActionIcon>}
                                  onClick={() => navigate(`/Admin/Blog/edit/${blog.id}`)}
                                />
                              </Tooltip>
                              <Tooltip label="View" hasArrow>
                                <IconButton
                                  aria-label="View"
                                  size="sm"
                                  variant="ghost"
                                  icon={<ActionIcon><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></ActionIcon>}
                                  onClick={() => {
                                    if (blog.status === 'published' && blog.slug) {
                                      window.open(`/blog/${blog.slug}`, '_blank', 'noopener,noreferrer');
                                      return;
                                    }
                                    navigate(`/Admin/Blog/preview/${blog.id}`);
                                  }}
                                />
                              </Tooltip>
                              <Tooltip label={blog.status === 'published' ? 'Unpublish' : 'Publish'} hasArrow>
                                <IconButton
                                  aria-label={blog.status === 'published' ? 'Unpublish' : 'Publish'}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme={blog.status === 'published' ? 'orange' : 'green'}
                                  isLoading={busyId === blog.id}
                                  icon={blog.status === 'published'
                                    ? <ActionIcon><path d="M17.9 17.9A10 10 0 0 1 6.1 6.1" /><path d="M2 12s3.5-6 10-6c1.6 0 3.1.3 4.4.9" /><path d="M22 12s-3.5 6-10 6c-1.6 0-3.1-.3-4.4-.9" /><path d="M4 4l16 16" /></ActionIcon>
                                    : <ActionIcon><path d="M12 19V5" /><path d="M5 12l7-7 7 7" /></ActionIcon>}
                                  onClick={() => toggleStatus(blog)}
                                />
                              </Tooltip>
                              <Tooltip label="Delete" hasArrow>
                                <IconButton
                                  aria-label="Delete"
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  icon={<ActionIcon><path d="M4 7h16" /><path d="M9 7V5h6v2" /><path d="M8 7l1 12h6l1-12" /></ActionIcon>}
                                  onClick={() => confirmDelete(blog)}
                                />
                              </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Card>

            {totalPages > 1 && (
              <Card borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <HStack justify="center" spacing={2} flexWrap="wrap">
                    <Button onClick={() => changePage(page - 1)} isDisabled={!pagination.has_prev_page || isLoading}>
                      Prev
                    </Button>
                    <Text fontSize="sm" color="gray.600">
                      Page {pagination.current_page} of {totalPages}
                    </Text>
                    <Button onClick={() => changePage(page + 1)} isDisabled={!pagination.has_next_page || isLoading}>
                      Next
                    </Button>
                  </HStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </Container>

        <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontWeight="700">Delete blog post</AlertDialogHeader>
              <AlertDialogBody>
                {`Delete "${pendingDelete?.title || 'this case study'}"? This cannot be undone.`}
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" ml={3} onClick={handleDelete} isLoading={isDeleting}>
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    </ChakraProvider>
  );
}

export default BlogManagement;
